/**
 * Utilidades para el sistema de tracking
 */

// Verificación de si estamos en el navegador o servidor
const isBrowser = typeof window !== 'undefined';

/**
 * Opciones para la función fetchWithTimeout
 */
export interface FetchTimeoutOptions extends RequestInit {
  timeout?: number;
}

/**
 * Wrapper para fetch con soporte para timeout
 * @param url URL a la que hacer petición
 * @param options Opciones para fetch, incluyendo timeout
 * @returns Promise con la respuesta
 */
export const fetchWithTimeout = async (
  url: string, 
  options: RequestInit & { timeout?: number } = {}
): Promise<Response> => {
  const { timeout = 8000, ...fetchOptions } = options;
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal
    });
    
    return response;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error(`Request timed out after ${timeout}ms`);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Detecta el tipo de dispositivo desde el userAgent
 * @returns información sobre el dispositivo
 */
export const detectDevice = (): {
  deviceType: string;
  browser: string;
  os: string;
  userAgent: string;
} => {
  if (!isBrowser) {
    return {
      deviceType: 'unknown',
      browser: 'unknown',
      os: 'unknown',
      userAgent: ''
    };
  }
  
  const userAgent = window.navigator.userAgent;
  let deviceType = 'desktop';
  let browser = 'unknown';
  let os = 'unknown';
  
  // Detectar dispositivo móvil/tablet
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
    deviceType = /iPad|tablet|Nexus 7/i.test(userAgent) ? 'tablet' : 'mobile';
  }
  
  // Detectar navegador
  if (userAgent.indexOf('Firefox') > -1) {
    browser = 'Firefox';
  } else if (userAgent.indexOf('SamsungBrowser') > -1) {
    browser = 'Samsung Browser';
  } else if (userAgent.indexOf('Opera') > -1 || userAgent.indexOf('OPR') > -1) {
    browser = 'Opera';
  } else if (userAgent.indexOf('Edge') > -1 || userAgent.indexOf('Edg') > -1) {
    browser = 'Edge';
  } else if (userAgent.indexOf('Chrome') > -1) {
    browser = 'Chrome';
  } else if (userAgent.indexOf('Safari') > -1) {
    browser = 'Safari';
  } else if (userAgent.indexOf('MSIE') > -1 || userAgent.indexOf('Trident/') > -1) {
    browser = 'Internet Explorer';
  }
  
  // Detectar sistema operativo
  if (userAgent.indexOf('Windows') > -1) {
    os = 'Windows';
  } else if (userAgent.indexOf('Mac OS') > -1) {
    os = 'MacOS';
  } else if (userAgent.indexOf('Linux') > -1) {
    os = 'Linux';
  } else if (userAgent.indexOf('Android') > -1) {
    os = 'Android';
  } else if (userAgent.indexOf('iOS') > -1 || /iPhone|iPad|iPod/i.test(userAgent)) {
    os = 'iOS';
  }
  
  return { deviceType, browser, os, userAgent };
};

/**
 * Extrae parámetros UTM de la URL actual
 * @returns Objeto con los parámetros UTM encontrados
 */
export const extractUtmParams = (): {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
} => {
  if (!isBrowser) {
    return {};
  }
  
  const urlParams = new URLSearchParams(window.location.search);
  
  return {
    utmSource: urlParams.get('utm_source') || undefined,
    utmMedium: urlParams.get('utm_medium') || undefined,
    utmCampaign: urlParams.get('utm_campaign') || undefined,
    utmContent: urlParams.get('utm_content') || undefined,
    utmTerm: urlParams.get('utm_term') || undefined
  };
};

/**
 * Obtiene el referrer para el tracking
 * @returns URL del referrer o null
 */
export const getReferrer = (): string | null => {
  if (!isBrowser) {
    return null;
  }
  
  return document.referrer || null;
};

/**
 * Genera un ID único simple
 * @returns ID único
 */
export const generateId = (): string => {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15) + 
         Date.now().toString(36);
};

/**
 * Genera un ID único
 * @returns ID único
 */
export const generateUniqueId = (): string => {
  // Usamos Date.now() + un número aleatorio y lo convertimos a base36
  return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Obtiene el valor de una cookie
 * @param name Nombre de la cookie
 */
export const getCookie = (name: string): string | null => {
  if (!isBrowser) {
    return null;
  }
  
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  
  return null;
};

/**
 * Establece una cookie
 * @param name Nombre de la cookie
 * @param value Valor de la cookie
 * @param days Días de expiración
 */
export const setCookie = (name: string, value: string, days = 30): void => {
  if (!isBrowser) {
    return;
  }
  
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
}; 